/******************************************************************************
 * Scene3D Hook
 * version: 1.0.1, release: 10.06.2019
 * author: albert lang, albert@jumptomorrow.com
 * ----------------------------------------------------------------------------
 * description:
 * 
 * dependencies: none
 * 
 * todos:
 * 
 * links:
 * render layer: https://codesandbox.io/s/0eog7y?file=/src/App.tsx
******************************************************************************/

/*
	Effekt: Effekt / OKR
	Effort: Aufwand / Investieren
	Fortschritt: Progress
	Dependencies: 
*/

import React, { useEffect, useState, useRef, useMemo } from 'react';
import Store from '../Store';

import { Canvas, useFrame, useLoader } from '@react-three/fiber'
import { Ring, Html, PresentationControls, Cylinder, Line } from '@react-three/drei';
import { TextureLoader } from 'three/src/loaders/TextureLoader'
import * as THREE from 'three';
// import { DepthOfField, EffectComposer } from "@react-three/postprocessing";
import './Scene3D.scss';
import { useSpring, a } from '@react-spring/three'
import { ButtonBar, Switch } from '../lib.r2/ui/ui-controls';
import DateFormat from '../lib.r2/utils/DateFormat';
import arrowBack from '../assets/Polygon_1.svg';


// const segment = 'assets/Segment.png'
// const textWire = 'assets/wire_texture_white_256.png'

const p2 = Math.PI * 2
const grad = Math.PI / 180
const deg90 = Math.PI / 2



//============================================================================= Scene3D Hook
export default function Scene3D(props) {
	return (
		<div className='full-view'>
			<Backdrop />
			<Scene data={props.data} />
			<Overlay data={props.data} />
		</div>
	);
}



//============================================================================= Scene
const Scene = ({data}) => {
	const isActive = Store.use('isActive')

	return (
		<Canvas
			camera={{fov:45, position:[0, 0, 15]}}
			>
			<ambientLight />
			<pointLight position={[15, 15, 15]} />

			<PresentationControls
				enabled={isActive}
				global={true}
				>
				<Stage data={data} />

			</PresentationControls>
		</Canvas>
	)
}



// ============================================================================ Stage
const Stage = ({data}) => {
	const [isOn, setOn] = useState(true)
	const num = data?.projects?.length || 0
	const deg = num? 360 / num : 0

	// const segment = Store.use('segment') ?? null
	// const project = Store.use('project') ?? null
	const [segment, project] = Store.use(['segment', 'project'])


	const initStage = e => {
		if (!e || Store.stage) return
		console.log('---Scene3D complete!', e);
		Store.stage = e

		Store.onChange(updateView)
	}


	useFrame(({ camera }) => {
		if (!Store.stage) return
	
		let x = Store.stage.parent.rotation.x
		let y = Store.stage.parent.rotation.y
	
		if (Store.logElement){
			Store.logElement.innerText = 'x:' + x.toFixed(2) + ' y: ' + y.toFixed(2)
		}
	
		if (x > 0.45){		//off
			if (isOn){
				// console.log('---switch off')
				setOn(false)
				Store.topElement.style.opacity = 0
				Store.subElement.style.opacity = 0
			}
		}else{
			if (!isOn){
				// console.log('---switch on')
				setOn(true)
				Store.topElement.style.opacity = 1
				Store.subElement.style.opacity = 1
			}
		}
	})

	
	const updateView = (state, old) => {
		if (state.segment === old.segment && state.project === old.project) return	//no changes
		// console.log('...update', state)

		if (state.segment || state.project){		//segment or project on
			if (!old.segment){						//first switch from 3d to segment view
				let last = Store.last = [
					Store.stage.parent.rotation.x,
					Store.stage.parent.rotation.y % p2,
				]
				last[1] = last[1] > Math.PI? last[1] - p2 : last[1] < -Math.PI? last[1] + p2 : last[1]
			}
			api.start({
				from: {rot: Store.last},
				to: {rot: state.project? [deg90, 0] : [0,0]}
			})
		}else{										//segment off | back to 3d
			if (api && Store.last){
				api.start({
					to: {rot: Store.last}
				})
			}
		}
	}


	const [, api] = useSpring(() => ({
		from: {rot: [0, 0]},
		onChange : e => {
			Store.stage.parent.rotation.x = e.value.rot[0]
			Store.stage.parent.rotation.y = e.value.rot[1]
		}
	}))


	return (
		<group name='main' ref={initStage}>
			{data.projects.map((e, i) => <Segment
				key={i}
				id={i}
				arc={data.projects.length === 1? 180 : i * deg}
				deg={deg}
				data={e}
				// selected={segment ?? null}
				selected={segment === null? null : e === segment}
			/>)}

			<Disks />

			{!project && <Sphere />}

			<DepLines />

		</group>
	)
}


// ============================================================================ DepLines
const DepLines = e => {
	const [depShow, depMode, isActive, selectedYear] = Store.use(['depShow', 'depMode', 'isActive', 'selectedYear'])

	const [lines, setLines] = useState([])

	useSpring({
		// mode  	: depShow? depMode : 10,
		// mode  	: depMode,
		mode  	: depMode + '.' + selectedYear,

		onChange : e => {
			let l = []

			Store.dependencies?.forEach(e => {
				let from = Store.projectPoints[e.pivot.source_project_id]
				let to = Store.projectPoints[e.pivot.target_project_id]
	
				if (from?.pos_current && to?.pos_current){
					l.push([from.pos_current, to.pos_current])
				}
			})

			setLines(l)
		}
	})


	useEffect(() => {
		let l = []

		let pos = depMode === 0? 'pos_class' :
			depMode === 1? 'pos' :
			depMode === 2? 'pos_impact' :
			depMode === 3? 'pos_risk' :	'pos'

		Store.dependencies?.forEach(e => {
			let from = Store.projectPoints[e.pivot.source_project_id]
			let to = Store.projectPoints[e.pivot.target_project_id]

			if (from?.[pos] && to?.[pos]){
				l.push([from[pos], to[pos]])
			}
		})

		setLines(l)

	}, [selectedYear])


	// if (!isActive) return null
	if (!depShow || !isActive) return null


	return (
		<group>
			{lines.map((p, i) => <Line key={i}
				points={p}
				color={'#ff0'}
				lineWidth={3}
				transparent
				dashed
				dashSize={0.1}
				gapSize={0.05}
				opacity={0.3}
				// opacity={(depShow && isActive)? 0.3 : 0.1}
				// visible={depShow && isActive}
			/>)}
		</group>
	)
}



// ---------------------------------------------------------------------------- DepControl
const DepControl = () => {
	const [depShow, depMode] = Store.use(['depShow', 'depMode'])

	return (
		<div id='info-control'>
			<div className='ic-header'>
				<div>{Store.getLabel('3D_DEPENDENCIES')}</div>
				<Switch selected={depShow} onChange={e => Store.set({depShow:e})} />
			</div>
			<ButtonBar
				content={[
					Store.getLabel('3D_TYPE').toUpperCase(),
					'NPS',
					Store.getLabel('3D_HIGHEST_IMPACT').toUpperCase(),
					Store.getLabel('3D_HIGH_RISK').toUpperCase(),
				]}
				toggle
				value={depMode}
				onChange={e => Store.set({'depMode':e})}
			/>
		</div>
	)
}


// const LI = 'Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.'
const title = ['BESCHREIBUNG', 'EFFEKT / OKR', 'VORTEIL / ERLEBEN', 'ERGEBNIS / LIEFERN', 'TÄTIGKEIT / MACHEN', 'AUFWAND / INVESTIEREN']


// ---------------------------------------------------------------------------- Scope
const Scope = ({data}) => {
	// console.log('---scope data:', data)

	const [selected, setSelected] = useState(0)
	const [text, setText] = useState(data.description)

	return (
		<div className='scope-view'>
			<div className='scope-header'>
				<h3>{data.name}</h3>
				<div>SCOPE</div>
			</div>
			<div className='scope-container'>
				<div className='scope-left'>
					<h4>{title[selected]}</h4>
					{/* <div>{text || (selected + ' ' + LI)}</div> */}
					<div>{text}</div>
				</div>
				<div className='scope-right'>
					<h5 className={'btn' + (selected === 0? ' selected' : '')} onClick={e => {
						setText(data.description)
						setSelected(0)
					}}>BESCHREIBUNG</h5>

					<h5 className={'btn' + (selected === 1? ' selected' : '')} onClick={e => {
						setText(data.effect)
						setSelected(1)
					}}>EFFEKT / OKR</h5>

					<h5 className={'btn' + (selected === 2? ' selected' : '')} onClick={e => {
						setText(data.benefits)
						setSelected(2)
					}}>VORTEIL / ERLEBEN</h5>

					<h5 className={'btn' + (selected === 3? ' selected' : '')} onClick={e => {
						setText(data.results)
						setSelected(3)
					}}>ERGEBNIS / LIEFERN</h5>

					<h5 className={'btn' + (selected === 4? ' selected' : '')} onClick={e => {
						setText(data.todos)
						setSelected(4)
					}}>TÄTIGKEIT / MACHEN</h5>

					<h5 className={'btn' + (selected === 5? ' selected' : '')} onClick={e => {
						setText(data.investment)
						setSelected(5)
					}}>AUFWAND / INVESTIEREN</h5>

				</div>
			</div>
		</div>
	)
}


// ---------------------------------------------------------------------------- UserCard
const ProStatus = ({data}) => {
	return (
		<div className='pro-status' >
			<div className='pro-status-item'>
				<div>{data?._impact_str}</div>
				<div>{Store.getLabel('3D_IMPACT').toUpperCase()}</div>
			</div>
			<div className='pro-status-item'>
				<div>{data?._invest_str}</div>
				<div>{Store.getLabel('3D_EXPENSE').toUpperCase()}</div>
			</div>
			<div className='pro-status-item'>
				<div>{data?._resources + ' ' + Store.getLabel('3D_MAN_DAYS', 'MD')}</div>
				<div>{Store.getLabel('3D_EXPENSE').toUpperCase()}</div>
			</div>
		</div>
	)
}


// ---------------------------------------------------------------------------- UserCard
const UserCard = ({data}) => {
	let name = ((data.first_name && data.first_name !== 'First name')? data.first_name + ' ' : '')
		+ ((data.last_name && data.last_name !== 'Last name')? data.last_name : '')

	return (
		<div className='pro-user'>
			<div className='pro-user-avatar'>
				{data.avatarAsset?.thumb_url &&
					<img src={data.avatarAsset.thumb_url} alt='' />
				}
			</div>
			<div className='pro-user-name'>{name}</div>
			<div className='pro-user-role'>{data._role || data.role.name}</div>
			{/* <div className='pro-user-role'>{data.userProjectRoles.name}</div> */}
			<a className='pro-user-email' href={'mailto:' + data.email}>{data.email}</a>
		</div>
	)
}


// ---------------------------------------------------------------------------- ProjectCard
const ProjectCard = ({data, onSelect}) => {
	return (
		<div className='pro-card' onClick={onSelect} >
			<div className='pro-item-title'>
				<div className='pro-item-name'>{data.name}</div>
				{/* <div className='pro-item-nps'>{data.nps}</div> */}
				<div className='pro-item-nps'>{data._nps}</div>
			</div>
			<ProItemLine label='Time' value={data._time} />
			{/* <ProItemLine label='Budget'value={0.7} /> */}
			{/* <ProItemLine label='Ressource' value={1} /> */}
			<ProItemLine label='Progress' value={data._progress} />
		</div>
	)
}


// ---------------------------------------------------------------------------- ProItemLine
const ProItemLine = ({label, value}) => {
	return (
		<div className='pro-item-line'>
			<div className='pro-item-label'>{label}</div>
			<div className='pro-item-value' >{Math.round(value) + '%'}</div>
			<div className='pro-item-progress'>
				<div style={{width:value + '%'}} />
			</div>
		</div>
	)
}


// ============================================================================ Segment
const pWidth = 8
const maxDis = 1.5
const offset = 5	//distance from beginning and end of the segment

const Segment = props => {
	const ref = useRef()
	const [points, setPoints] = useState([])
	const [down, setDown] = useState(null)
	const [hover, setHover] = useState(false)

	const [project, selectedYear] = Store.use('project', 'selectedYear')		//if a single project is selected

	useEffect(() => {		//calculate the point positions
		let segment = props.data
		let childs = segment.childs

		if (selectedYear !== 'ALL'){
			childs = segment.childs.filter(c => c._years.includes(selectedYear))
		}

		let num = childs.length
		let dis = num > 1? pWidth / (num - 1) : 0
		if (dis > maxDis) dis = maxDis
		let p2 = dis * (num - 1) / 2
		// let n = num-1

		// ---create array with random sorting
		let arr = Array.from({length: num}, (x, i) => i);
		arr.forEach((x, i) => {
			let j = Math.floor(Math.random() * (i + 1));
			[arr[i], arr[j]] = [arr[j], arr[i]];
		});

		let p = childs.map((pData, i) => {
			// let s = (i+1) / (num + 1)
			let s = (arr[i]+1) / (num + 1)		//random segment angle
			let a = (s * props.deg + props.arc) * grad
			// let c = pData._nps / 50 + 3 - 0.2	//1 - 5

			// point radius position: 3 = outer ring, 2.5 ring center (middle)
			let c = pData._nps / 50 + 2.5	//1 - 5
			let sa = Math.sin(a)
			let ca = Math.cos(a)
			let x = c * sa
			let z = c * ca

			// 'Standard', 'Differenzierung', 'Vorreiter', 'Gamechanger'
			let ct = Store.classes.indexOf(pData.class.toLowerCase()) + 1.5	// value for type
			let ci = 1 - 0.2 + pData._impact_level * 4 / (num-1)	// position for impact
			let cr = 1 - 0.2 + pData._risk_level * 4 / (num-1)		// position for risk

			let pItem = pData._pItem

			if (!pItem){
				pItem = {	//create project item
					id			: pData.id,
					pos			: [-x, 0, z],				// position for nps
					pos_select	: [num > 1? i * dis - p2 : 0, 0, 0],	//position if segment is selected
					label		: pData.name,
					// color		: colors[Math.floor(Math.random() * 4)],
					color		: colors[pData._color || 0],
					data		: pData,
					segment,
	
					pos_class	: [-ct*sa, 0, ct*ca],		// position for type
					pos_impact	: num === 1? [-x, 0, z] : [-ci*sa, 0, ci*ca],
					pos_risk	: num === 1? [-x, 0, z] : [-cr*sa, 0, cr*ca],
				}
				pData._pItem = pItem
			}

			Store.projectPoints[pData.id] = pItem	//append project

			if (pData.dependencies?.length){		//append dependencies
				pData.dependencies.forEach(d => {
					if (d.pivot.source_project_id === pData.id){
						// console.info('set dep.', d)
						Store.dependencies.push(d)
					}
				})
			}

			return pItem
		})

		setPoints(p)
		
	}, [selectedYear])


	const onSelect = e => {
		if (!ValidPointer(down, e)) return
		console.log('---set segment:', props.data)
		Store.set({segment:props.data, isActive:false})
	}


	// if (project) return null

	const seg = Math.floor(72 * props.deg / 360)

	return (
		<group>
		{(props.selected !== false && !project) &&
			<group>
				{points.map((e, i) => <PointLabel key={i} data={e} selected={props.selected} />)}
			</group>
		}

		<a.group
			name = {'segment-' + props.id}
			ref={ref}
			rotation={[0, -props.arc * grad, 0]}
			onClick={onSelect}
			onPointerDown={setDown}
			onPointerOver={e => setHover(true)}
			onPointerOut={e => setHover(false)}
			>
			<RingLabel deg={props.deg} title={props.data.name} />

			{!project &&
				<>
					<Line
						points={[[0, 0, 0], [0, 0, 5.6]]}
						color={'#fff'}
						lineWidth={1}
						transparent
						opacity={0.2}
					/>

					{/* ---click proxy */}
					<Ring args={[5, 0, seg, 1, 0, props.deg * grad]} rotation={[deg90, 0, deg90]} >
						<a.meshStandardMaterial
							color={'white'}
							transparent
							opacity={hover? 0.03 : 0}
							depthTest={false}
							depthWrite={false}
						/>
					</Ring>
				</>
			}
		</a.group>
		</group>
	)
}


const ValidPointer = (e0, e1) => {
	if (!e0 || !e1) return false
	if (e0.eventObject !== e1.eventObject) return false
	if (e0.layerX !== e1.layerX || e0.layerY !== e1.layerY) return false
	return true
}


// ============================================================================ RingLabel
const RingLabel = ({deg=90, title='TITLE'}) => {
	const project = Store.use('project')

	const seg = Math.floor(72 * deg / 360)
	const args = [5.5, 5, 0, seg, 1, true, 0, deg * -grad]

	const texture = useMemo(() => {
		var canvas = document.createElement('canvas');
		canvas.width = 512;
		canvas.height = 64;
		var ctx = canvas.getContext('2d');
		ctx.font = "30px Arial";
		ctx.fillStyle = "#5D7586";
		ctx.fillRect(0, 0, canvas.width, canvas.height)

		ctx.fillStyle = "#fffc";
		ctx.textAlign = "center";
		ctx.fillText(title.toUpperCase(), 256, 40);

		let txt = new THREE.Texture(canvas);
		txt.needsUpdate = true
		return txt

	}, [title])

	texture.center.set(0.5, 0.5)
	texture.repeat.set( deg / 45, 1 );
	texture.offset.set(0, project? 1 : 0)

	return (
		<Cylinder args={args}>
			<meshBasicMaterial map={texture} />
		</Cylinder>
	)
}


// blue, green, yellow, red
const colors = ['#bdcfc9', '#aad45b', '#ffd175', '#be4b68']


// ============================================================================ InfoLabel
const InfoLabel = () => {
	// const [data, setData] = useState({
	// 	ranking		: 12,
	// 	effect		: '5 MIO',
	// 	teamsize	: 5,
	// 	effort		: '250K\n50FTE'
	// })
	const timeout = useRef()
	const labelRef = useRef()
	const [data, setData] = useState()
	const [pData, setPData] = useState()

const isOver = useRef(false)

	const setLabelData = (el, pData) => {
		// console.log('---setData', el, pData, isOver.current)
		if (isOver.current) return

		clearTimeout(timeout.current)
		let label = labelRef.current

		if (!el || !pData){
			// setData(null)
			label.style.opacity = 0
			label.style.pointerEvents = 'none'

		}else{
			setPData(pData)
			setData(pData.data)

			let rect = el.parentNode.parentNode.getBoundingClientRect()

			label.style.transition = 'none'
			label.style.left = Math.max((rect.left - 180), 10) + 'px'
			label.style.top = Math.max((rect.top - 130), 10) + 'px'

			timeout.current = setTimeout(e => {
				label.style.transition = '0.2s'
				label.style.opacity = 1
				label.style.pointerEvents = 'all'
			}, 200)
		}
	}

	useEffect(() => {
		Store.setData = setLabelData
	}, [])


	const over = (e) => {
		if (isOver.current) return

		e.stopPropagation()
		isOver.current = true
	}

	const out = (e) => {
		if (e.target !== labelRef.current) return

		e.stopPropagation()
		isOver.current = false
		setLabelData(null)
	}
	

	const onSelect = (e) => {
		if (e.target.nodeName === 'A') return	//ignore href

		e.stopPropagation()
		isOver.current = false
		setLabelData(null)

		console.log('---set project:', pData)
		Store.set({project:pData, isActive:false})
	}


	return (
		<div className='info-label'
			ref={labelRef}
			onMouseOver={over}
			onMouseLeave={out}
			onClick={onSelect}
			>
			{/* <img src='assets/pro_label.png' alt='' /> */}
			<div className='cell c1'>
				<h3>{data?._npsRank}</h3>
				<div>RANKING</div>
			</div>
			<div className='cell c2'>
				<h3>{data?._impact_str}</h3>
				<div>WIRKUNG</div>
			</div>
			<div className='cell c3'>
				<h3>{data?.users?.length || 0}</h3>
				<div>TEAMSIZE</div>
			</div>
			<div className='cell c4'>
				<h4>{data?._invest_str}</h4>
				<h4>{data?._resources + ' ' + Store.getLabel('3D_MAN_DAYS', 'MD')}</h4>
				<div>AUFWAND</div>
			</div>
			{!!data?.users?.length &&
				<div className='cell c5'>
					{data.users.filter(u => u._owner).map((u, i) =>
						// <div key={i}>{u.first_name + ' ' + u.last_name}</div>
						// <div key={i}>{u.email}</div>
						<a key={i} href={'mailto:' + u.email}>{u.email}</a>
					)}
				</div>
			}
		</div>
	)
}


// ============================================================================ PointLabel
// data = pointData
const PointLabel = ({data, selected}) => {
	const depMode = Store.use('depMode')

	const springs = useSpring({
		// pos  : selected? data.selectPos : data.pos
		pos  : selected? data.pos_select :
			depMode === 0? data.pos_class :		// type
			depMode === 1? data.pos :			// nps
			depMode === 2? data.pos_impact :	// highest impact
			depMode === 3? data.pos_risk :		// high risk
			data.pos,

		onChange : e => {
			// console.log('>', data.id, e.value.pos)
			data.pos_current = e.value.pos
		}
	})

	const over = (e) => {
		e.stopPropagation()
		Store.setData(e.target, data)	//-> on
	}

	const out = (e) => {
		e.stopPropagation()
		setTimeout(() => {
			Store.setData(null)			//-> off
		}, 0)
	}

	const onSelect = (e) => {
		e.stopPropagation()
		console.log('---set project:', data)
		Store.set({project:data, isActive:false})
	}

	return(
		<a.mesh position={springs.pos}>
		<Html
			distanceFactor={8}
			zIndexRange={[100, 200]}
			>
			<div className='billboard'
				// onMouseOver={over}
				// onMouseOut={out}
				// onMouseDown={out}
				onClick={onSelect}
				>
				<div className='billboard-dot'
					style={{background:data.color}}
					onMouseOver={over}
					onMouseOut={out}
					onMouseDown={out}
					onClick={onSelect}
				/>
				<div className='billboard-word'>{data.label}</div>
			</div>
		</Html>
		</a.mesh>
	)
}


// ============================================================================ Overlay
// data = strategy-data
const Overlay = ({data}) => {
	const [scope, setScope] = useState(false)
	
	const [segment, project, subProject, isActive] = Store.use(['segment', 'project', 'subProject', 'isActive'])
	const pData = subProject || project?.data	//project-data

	
	useEffect(() => {
		Store.logElement = document.getElementById('info-log')
		Store.topElement = document.getElementById('info-top')
		Store.subElement = document.getElementById('info-sub')
	}, [])


	const onBack = e => {
		if (scope) setScope(false)
		else if (subProject) Store.set({subProject:null})
		else if (project) Store.set({project:null, isActive:!segment})
		else Store.set({segment:null, isActive:true})
	}


	const onSelectProject = e => {
		console.log('---set sub-project:', e)
		Store.set({subProject:e})
	}


	const onSetScope = e => {
		setScope(true)
	}


	return (
		<div id='info-overlay'>
			<InfoLabel />	{/* hover overlay -> from PointLabel.over */}

			{!isActive &&
				<>
					<h2 id='title-left'>{segment?.name || project.segment.name}</h2>
					<h2 id='go-back' onClick={onBack}>
						<img src={arrowBack} alt='' />
						<span>{Store.getLabel('3D_BACK').toUpperCase()}</span>
					</h2>
				</>
			}

			<div className='info-container'>
				<div id='info-title'>
					<h1>{data.name}</h1>
				</div>
				<div id='info-top'>
					{segment?
						<h1>{segment._impact_str}</h1>
						:
						<h1>{data._impact_str}</h1>
					}
					<div>{Store.getLabel('3D_IMPACT').toUpperCase()}</div>
				</div>

				<div id='info-sub'>
					<h1>{(segment? segment._resources : data._resources) + ' ' + Store.getLabel('3D_MAN_DAYS', 'MD')}</h1>
					<h1>{segment? segment._invest_str : data._invest_str}</h1>
					<div>{Store.getLabel('3D_EXPENSE').toUpperCase()}</div>
				</div>

				{(pData && !scope) &&
					<div className='pro-content'>
						<div id='pro-nps'
							style={{color:colors[pData._color]}}
							>{pData._nps}</div>

						<div id='pro-rank'>{pData._npsRank + '/' + project.segment.childs.length}</div>
						<h3 id='pro-name'>{pData.name}</h3>
						<div id='pro-time'>{DateFormat(pData.start_date, 'mm/yy') + ' bis ' + DateFormat(pData.end_date, 'mm/yy')}</div>

						<ProStatus data={pData} />

						<div className='pro-users'>
							{pData.users?.map((e, i) => <UserCard key={i} data={e} />)}
						</div>

						<div className='pro-childs'>
							{subProject ?
								<ProjectCard data={subProject} />
								:
								pData.childs?.map((e, i) =>
									<ProjectCard
										key={i}
										data={e}
										onSelect={() => onSelectProject(e)}
									/>
								)
							}
						</div>

						{subProject &&
							<div className='style-button' onClick={onSetScope}>PROJECT SCOPE</div>
						}
					</div>
				}

				{scope && <Scope data={subProject} />}
			</div>

			{isActive && <DepControl />}

			{/* <div id='info-log' /> */}
		</div>
	)
}



// ============================================================================ Sphere
const Sphere = (props) => {
	const textMap = useLoader(TextureLoader, 'assets/wire_texture_white_512.png')
	// const textMap = useTexture('assets/wire_texture_white_256.png')
	textMap.repeat.set(10, 10)
	textMap.wrapS = THREE.RepeatWrapping
	textMap.wrapT = THREE.RepeatWrapping

	return (
		<group name='sphere'>
			<mesh>
				<sphereGeometry args={[5, 64, 64]} />
				<meshStandardMaterial
					color={new THREE.Color(0xcccccc)}	// https://threejs.org/docs/index.html?q=colo#api/en/math/Color
					transparent
					opacity={0.05}
					depthTest={false}
					depthWrite={false}
					renderOrder={1}
				/>
			</mesh>

			<mesh
				>
				<sphereGeometry args={[5, 64, 64]} />
				<meshStandardMaterial
					map={textMap}
					transparent
					opacity={0.1}
					side={THREE.DoubleSide}
					depthTest={false}
					depthWrite={false}
					renderOrder={1}
				/>
			</mesh>
		</group>
	)
}


// ============================================================================ Disks
const Disks = props => {
	const project = Store.use('project')

	const springs = useSpring({
		scale : project? 2.4 : 1,
		opacity : project? 0.1 : 0.2,
		opacity1 : project? -0.1 : 0.15,
		opacity2 : project? -0.1 : 0.1,
	})


	return (
		<group name='disks' rotation={[deg90, 0, 0]}>
			<a.mesh scale={springs.scale} position={[0, 0, -0.01]}>
				<Ring args={[2, 0, 64, 1]}>
					<a.meshStandardMaterial
						color={'white'}
						transparent
						opacity={springs.opacity}
					/>
				</Ring>
				<Ring args={[3, 2, 64, 1]}>
					<a.meshStandardMaterial
						color={'white'}
						transparent
						opacity={springs.opacity1}
					/>
				</Ring>
				<Ring args={[4, 3, 64, 1]}>
					<a.meshStandardMaterial
						color={'white'}
						transparent
						opacity={springs.opacity2}
					/>
				</Ring>
			</a.mesh>
		</group>
	)
}


// ============================================================================ Overlay
const Backdrop = e => {
	const [segment, project] = Store.use(['segment', 'project'])

	return (
		<div id='backdrop'>
			<div className='info-container'>
				<div className={'segments' + ((segment && !project)? ' open' : '')}>
					<div id='segment-left'>
						<div id='segment-r1' />
						<div id='segment-r2' />
						<div id='segment-r3' />
					</div>
					<div id='segment-right'>
						<div id='segment-r1' />
						<div id='segment-r2' />
						<div id='segment-r3' />
					</div>
				</div>

			</div>
		</div>
	)
}
